
import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { TravelMateResponse, Hotel, SearchQuery } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { generateHotelPath } from '../utils/slugUtils';

interface TravelMateAgentProps {
    advice: TravelMateResponse;
    currency: string;
    results?: Hotel[]; // Optional list of actual hotel objects to match against
    searchQuery?: SearchQuery;
}

export const TravelMateAgent: React.FC<TravelMateAgentProps> = ({ advice, currency, results, searchQuery }) => {
    const { t } = useLocalization();
    const navigate = useNavigate();
    const isNegotiation = advice.status === 'negotiation_needed';
    const percentGap = advice.budget_analysis.gap > 0 && advice.budget_analysis.user_budget > 0
        ? Math.round((advice.budget_analysis.gap / advice.budget_analysis.user_budget) * 100)
        : 0;

    const handleCardClick = (cardId: string | number, cardDetails: string) => {
        if (!results || !searchQuery) return;

        // Try to find the hotel in the results based on ID first, then fuzzy match
        const matchedHotel = results.find(h =>
            h.id === cardId ||
            cardDetails.toLowerCase().includes(h.name.toLowerCase()) ||
            h.name.toLowerCase().includes(cardDetails.toLowerCase())
        );

        if (matchedHotel) {
            navigate(generateHotelPath(matchedHotel), { state: { hotel: matchedHotel, searchQuery } });
        } else {
            console.warn("Could not match TravelMate card to a specific hotel result.");
        }
    };

    return (
        <div className="bg-gradient-to-br from-indigo-50/50 to-blue-50/50 rounded-3xl border border-indigo-100/50 p-6 shadow-sm mb-8 animate-fadeInUp">

            {/* Header / Agent Chat */}
            <div className="flex gap-4 items-start mb-6">
                <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-indigo-600 to-indigo-500 flex items-center justify-center shadow-xl border-2 border-white animate-bounce-slow">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                </div>
                <div className="flex-grow space-y-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-black text-indigo-900 tracking-tight">{t('travelmate_ai')}</h3>
                            <span className="text-[9px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-black uppercase tracking-widest border border-indigo-200/50">{t('beta')}</span>
                        </div>
                        <div className="bg-white p-5 rounded-2xl rounded-tl-none shadow-sm text-gray-700 text-[15px] leading-relaxed border border-indigo-50/50 relative overflow-hidden group">
                            <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            {advice.assistant_reply}
                        </div>
                    </div>

                    {/* Suggestion Chips */}
                    {advice.suggestion_chips && advice.suggestion_chips.length > 0 && (
                        <div className="flex flex-wrap gap-2 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
                            {advice.suggestion_chips.map((chip, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        // Emit event or handle interaction if needed
                                        // For now, these are mainly visual in the Agent view
                                    }}
                                    className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-100 px-3 py-1.5 rounded-full text-xs font-bold transition-all transform hover:-translate-y-0.5 active:scale-95 whitespace-nowrap"
                                >
                                    {chip.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Budget Analysis Bar */}
            {isNegotiation && (
                <div className="mb-8 px-2 md:px-16 animate-fadeIn" style={{ animationDelay: '0.5s' }}>
                    <div className="flex justify-between text-[10px] font-black text-gray-400 mb-2 uppercase tracking-[0.15em]">
                        <span>{t('your_budget')}</span>
                        <span className="text-red-500">{t('gap')} (+{percentGap}%)</span>
                        <span>{t('market_avg')}</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex relative shadow-inner">
                        {/* Budget Segment */}
                        <div className="h-full bg-gradient-to-r from-green-500 to-green-400 shadow-[0_0_10px_rgba(34,197,94,0.3)]" style={{ width: '60%' }}></div>
                        {/* Gap Segment */}
                        <div className="h-full bg-gradient-to-r from-red-400 to-red-300 shadow-[0_0_10px_rgba(248,113,113,0.3)]" style={{ width: '40%' }}></div>

                        {/* Tooltip Marker */}
                        <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-sm" style={{ left: '60%' }}></div>
                    </div>
                    <div className="flex justify-between mt-2 text-[11px] font-black text-gray-500">
                        <span>{advice.budget_analysis.user_budget.toLocaleString()} {currency}</span>
                        <span>{advice.budget_analysis.original_cost.toLocaleString()} {currency}</span>
                    </div>
                </div>
            )}

            {/* Interactive Strategy Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {advice.interactive_cards.map((card, cIdx) => (
                    <div
                        key={card.id || cIdx}
                        className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-500 group cursor-pointer flex flex-col transform hover:-translate-y-2 animate-fadeInUp"
                        style={{ animationDelay: `${0.6 + cIdx * 0.1}s` }}
                        onClick={() => handleCardClick(card.id, card.details_summary)}
                    >
                        <div className={`px-4 py-2 text-[9px] font-black uppercase tracking-[0.2em] text-white text-center shadow-sm
                            ${card.badge.toLowerCase().includes('luxury') ? 'bg-gradient-to-r from-purple-600 to-indigo-600' :
                                card.badge.toLowerCase().includes('budget') ? 'bg-gradient-to-r from-emerald-600 to-teal-600' :
                                    'bg-gradient-to-r from-amber-600 to-orange-600'}
                        `}>
                            {card.badge}
                        </div>
                        <div className="p-5 flex-grow flex flex-col justify-between">
                            <div>
                                <h4 className="font-black text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors line-clamp-1 text-lg tracking-tight">{card.title}</h4>
                                <p className="text-xs text-gray-400 font-bold mb-4 uppercase tracking-wider">{card.details_summary}</p>
                                <div className="relative p-3 rounded-xl bg-indigo-50/50 border border-indigo-100/50 mb-4 overflow-hidden group/quote">
                                    <div className="absolute -top-1 -left-1 text-4xl text-indigo-200/50 font-serif leading-none italic select-none">"</div>
                                    <p className="text-xs text-indigo-900 font-semibold italic relative z-10 leading-relaxed">
                                        {card.why_this_option}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-2 pt-4 border-t border-gray-50 flex justify-between items-end">
                                <div>
                                    <span className="text-[9px] text-gray-400 block font-black uppercase tracking-widest mb-1">{t('est_total')}</span>
                                    <span className="font-black text-gray-900 text-lg">{card.total_price.toLocaleString()} {currency}</span>
                                </div>
                                <div className="flex gap-2">
                                    {results && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCardClick(card.id, card.details_summary);
                                            }}
                                            className="px-4 py-2 bg-indigo-600 text-white text-[10px] font-black rounded-lg hover:bg-indigo-700 transition-all shadow-lg hover:shadow-indigo-500/30 transform active:scale-95 uppercase tracking-wider"
                                        >
                                            {t('view_hotel')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes bounceSlow {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                }
                .animate-fadeInUp { animation: fadeInUp 0.5s ease-out forwards; }
                .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
                .animate-bounce-slow { animation: bounceSlow 3s ease-in-out infinite; }
            `}</style>
        </div>
    );
};
